// TeachESLPro shared UI primitives — mock app chrome, cards, avatars.
// Exports: TepLogo, AppShell, Sidebar, TopBar, Avatar, Chip, ProgressBar, Cursor,
// Toast, PhoneFrame, ButtonPrimary, ButtonGhost, FlagDot.

function TepLogo({ size = 18, color = "#1f3d2b", showWord = true }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="2" y="2" width="20" height="20" rx="5" fill={color}/>
        <path d="M7 9.5h10M12 9.5v8" stroke="#faf6ed" strokeWidth="1.8" strokeLinecap="round"/>
        <circle cx="17" cy="7" r="2" fill="#c7b89a"/>
      </svg>
      {showWord && (
        <div style={{ fontFamily: 'var(--display)', fontSize: size + 2, fontWeight: 500, letterSpacing: "-0.01em", color }}>
          TeachESL<span style={{ color: "#c7b89a" }}>Pro</span>
        </div>
      )}
    </div>
  );
}

function Avatar({ name = "AB", color = "#4a6b52", size = 28, ring }) {
  const initials = name.split(" ").map(w => w[0]).slice(0,2).join("").toUpperCase();
  return (
    <div style={{
      width: size, height: size, borderRadius: size,
      background: color, color: "#faf6ed",
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      fontSize: size * 0.40, fontWeight: 600, letterSpacing: "0.02em",
      fontFamily: 'var(--ui)',
      boxShadow: ring ? `0 0 0 2px ${ring}, 0 0 0 4px #faf6ed` : "none",
      flexShrink: 0,
    }}>
      {initials}
    </div>
  );
}

function FlagDot({ country = "ES", size = 12 }) {
  // Tiny country dot — uses simple stripes
  const palettes = {
    ES: ["#c60b1e", "#ffc400", "#c60b1e"], // Spain
    BR: ["#009b3a", "#ffdf00", "#009b3a"], // Brazil
    JP: ["#fff", "#bc002d", "#fff"],
    KR: ["#fff", "#cd2e3a", "#fff"],
    FR: ["#0055a4", "#fff", "#ef4135"],
    IT: ["#008c45", "#fff", "#cd212a"],
    DE: ["#000", "#dd0000", "#ffce00"],
    VN: ["#da251d", "#ffff00", "#da251d"],
    SA: ["#006c35", "#fff", "#006c35"],
    TR: ["#e30a17", "#fff", "#e30a17"],
  };
  const colors = palettes[country] || ["#999","#bbb","#999"];
  return (
    <span style={{
      display: "inline-block", width: size, height: size, borderRadius: size,
      background: `linear-gradient(180deg, ${colors[0]} 0 33%, ${colors[1]} 33% 66%, ${colors[2]} 66% 100%)`,
      border: "1px solid rgba(0,0,0,0.08)",
      flexShrink: 0,
    }}/>
  );
}

function Chip({ children, color = "sage", size = "md", style = {} }) {
  const palettes = {
    sage: { bg: "#e6ede0", fg: "#1f3d2b", line: "#cad8c0" },
    tan:  { bg: "#f0e6d2", fg: "#5e4f30", line: "#dccfae" },
    cream:{ bg: "#faf6ed", fg: "#161a17", line: "#e8e3d2" },
    sky:  { bg: "#e6efea", fg: "#2a4d56", line: "#cfded5" },
    plum: { bg: "#efe4ec", fg: "#5a3358", line: "#d8c4d4" },
    ink:  { bg: "#1f3d2b", fg: "#faf6ed", line: "#1f3d2b" },
  };
  const p = palettes[color] || palettes.sage;
  const sizes = { sm: { fs: 11, py: 3, px: 8 }, md: { fs: 12.5, py: 4, px: 10 }, lg: { fs: 14, py: 6, px: 12 } };
  const s = sizes[size];
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      background: p.bg, color: p.fg,
      padding: `${s.py}px ${s.px}px`,
      borderRadius: 999,
      fontSize: s.fs, fontWeight: 500, letterSpacing: "-0.005em",
      border: `1px solid ${p.line}`,
      whiteSpace: "nowrap",
      ...style,
    }}>{children}</span>
  );
}

function ProgressBar({ value = 0, color = "#1f3d2b", track = "#e8e3d2", height = 6, radius = 4 }) {
  return (
    <div style={{ width: "100%", height, background: track, borderRadius: radius, overflow: "hidden" }}>
      <div style={{
        width: `${Math.max(0, Math.min(100, value))}%`,
        height: "100%", background: color, borderRadius: radius,
        transition: "width 200ms linear",
      }}/>
    </div>
  );
}

function ButtonPrimary({ children, style = {}, icon }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 8,
      background: "#1f3d2b", color: "#faf6ed",
      padding: "9px 16px", borderRadius: 8,
      fontSize: 13, fontWeight: 500, letterSpacing: "-0.005em",
      border: "1px solid #1f3d2b",
      boxShadow: "0 1px 0 rgba(0,0,0,0.05)",
      ...style,
    }}>
      {icon}{children}
    </span>
  );
}

function ButtonGhost({ children, style = {}, icon }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      background: "#ffffff", color: "#1f3d2b",
      padding: "8px 12px", borderRadius: 8,
      fontSize: 12.5, fontWeight: 500,
      border: "1px solid #d8d2bd",
      ...style,
    }}>
      {icon}{children}
    </span>
  );
}

function Cursor({ x, y, scale = 1, label, opacity = 1 }) {
  return (
    <div style={{
      position: "absolute", left: x, top: y,
      pointerEvents: "none", transform: `scale(${scale})`,
      transformOrigin: "top left", opacity, zIndex: 100,
      transition: "transform 80ms",
    }}>
      <svg width="22" height="22" viewBox="0 0 22 22" style={{ filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.25))" }}>
        <path d="M3 2 L3 17 L7.5 13 L10 19 L12 18 L9.5 12 L15 12 Z" fill="#161a17" stroke="#faf6ed" strokeWidth="1.2" strokeLinejoin="round"/>
      </svg>
      {label && (
        <div style={{
          position: "absolute", left: 22, top: 18,
          background: "#161a17", color: "#faf6ed",
          padding: "3px 8px", borderRadius: 4,
          fontSize: 10.5, fontFamily: "var(--mono)", whiteSpace: "nowrap",
        }}>{label}</div>
      )}
    </div>
  );
}

// Sidebar — left nav for the teacher app
function Sidebar({ active = "students", width = 200 }) {
  const items = [
    { id: "home", label: "Home", icon: "M3 11L11 4L19 11M5 9V18H17V9", count: null },
    { id: "students", label: "Students", icon: "M11 11C13 11 14.5 9.5 14.5 7.5C14.5 5.5 13 4 11 4C9 4 7.5 5.5 7.5 7.5C7.5 9.5 9 11 11 11ZM4 18C4 14.5 7 12 11 12C15 12 18 14.5 18 18", count: 12 },
    { id: "pathways", label: "Pathways", icon: "M5 6L9 6M5 11L13 11M5 16L17 16", count: null },
    { id: "library", label: "Resource Library", icon: "M5 4H17V18H5z M5 8H17 M9 4V18", count: null },
    { id: "lessons", label: "Lesson Studio", icon: "M3 4H19V14H3z M7 18H15", count: null },
    { id: "schedule", label: "Schedule", icon: "M5 5H17V17H5z M5 9H17 M8 3V7 M14 3V7", count: 4 },
    { id: "messages", label: "Messages", icon: "M4 5H18V14H10L6 17V14H4z", count: 2 },
  ];
  return (
    <div style={{
      width, height: "100%",
      background: "#fbf8ee",
      borderRight: "1px solid #e8e3d2",
      display: "flex", flexDirection: "column",
      flexShrink: 0,
    }}>
      <div style={{ padding: "16px 16px 14px", borderBottom: "1px solid #e8e3d2" }}>
        <TepLogo size={17}/>
      </div>
      <div style={{ padding: "12px 8px", flex: 1 }}>
        <div style={{ fontSize: 10.5, color: "#8a8f86", fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", padding: "6px 10px 8px" }}>Teach</div>
        {items.map(it => (
          <div key={it.id} style={{
            display: "flex", alignItems: "center", gap: 10,
            padding: "7px 10px", borderRadius: 6,
            background: active === it.id ? "#e6ede0" : "transparent",
            color: active === it.id ? "#1f3d2b" : "#2c322d",
            fontSize: 13, fontWeight: active === it.id ? 600 : 500,
            marginBottom: 2,
          }}>
            <svg width="16" height="16" viewBox="0 0 22 22" fill="none">
              <path d={it.icon} stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
            </svg>
            <span style={{ flex: 1 }}>{it.label}</span>
            {it.count != null && (
              <span style={{ fontSize: 10.5, color: "#6b7269", background: "#f3eedf", padding: "1px 6px", borderRadius: 999, fontFamily: "var(--mono)" }}>{it.count}</span>
            )}
          </div>
        ))}
      </div>
      <div style={{ padding: 12, borderTop: "1px solid #e8e3d2", display: "flex", alignItems: "center", gap: 10 }}>
        <Avatar name="Sarah Chen" color="#2f5740" size={28}/>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12.5, fontWeight: 600 }}>Sarah Chen</div>
          <div style={{ fontSize: 10.5, color: "#8a8f86" }}>Senior Tutor</div>
        </div>
      </div>
    </div>
  );
}

function TopBar({ title, breadcrumb, right }) {
  return (
    <div style={{
      height: 56, padding: "0 24px",
      borderBottom: "1px solid #e8e3d2",
      background: "#faf6ed",
      display: "flex", alignItems: "center", gap: 16, flexShrink: 0,
    }}>
      <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 12 }}>
        {breadcrumb && (
          <div style={{ fontSize: 11.5, color: "#8a8f86", fontFamily: "var(--mono)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
            {breadcrumb}
          </div>
        )}
        <div style={{ fontFamily: "var(--display)", fontSize: 22, fontWeight: 500, letterSpacing: "-0.015em" }}>{title}</div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>{right}</div>
    </div>
  );
}

function AppShell({ active, title, breadcrumb, topRight, children, sidebarWidth = 200 }) {
  return (
    <div style={{
      width: "100%", height: "100%",
      display: "flex", background: "#faf6ed",
      fontFamily: "var(--ui)", color: "#161a17",
    }}>
      <Sidebar active={active} width={sidebarWidth}/>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
        <TopBar title={title} breadcrumb={breadcrumb} right={topRight}/>
        <div style={{ flex: 1, overflow: "hidden", position: "relative" }}>{children}</div>
      </div>
    </div>
  );
}

// Phone frame for student-facing scenes
function PhoneFrame({ width = 320, height = 660, children, style = {} }) {
  return (
    <div style={{
      width, height,
      background: "#1a1f1c", borderRadius: 36, padding: 8,
      boxShadow: "0 30px 80px -30px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.04) inset",
      position: "relative",
      ...style,
    }}>
      <div style={{
        width: "100%", height: "100%",
        background: "#faf6ed", borderRadius: 28, overflow: "hidden",
        position: "relative",
      }}>
        {/* notch */}
        <div style={{
          position: "absolute", top: 8, left: "50%", transform: "translateX(-50%)",
          width: 100, height: 22, background: "#1a1f1c", borderRadius: 14, zIndex: 5,
        }}/>
        {children}
      </div>
    </div>
  );
}

function Toast({ children, x, y, color = "sage", show = true, opacity = 1 }) {
  const palettes = {
    sage: { bg: "#1f3d2b", fg: "#faf6ed", icon: "#c7b89a" },
    tan: { bg: "#f3eedf", fg: "#161a17", icon: "#1f3d2b" },
  };
  const p = palettes[color];
  return (
    <div style={{
      position: "absolute", left: x, top: y,
      display: "inline-flex", alignItems: "center", gap: 10,
      background: p.bg, color: p.fg,
      padding: "10px 16px", borderRadius: 10,
      fontSize: 13, fontWeight: 500,
      boxShadow: "0 18px 40px -16px rgba(0,0,0,0.4)",
      opacity: show ? opacity : 0,
      transition: "opacity 200ms",
      zIndex: 50,
    }}>
      <svg width="14" height="14" viewBox="0 0 14 14"><path d="M3 7l3 3 5-6" stroke={p.icon} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/></svg>
      {children}
    </div>
  );
}

export {
  TepLogo,
  Avatar,
  FlagDot,
  Chip,
  ProgressBar,
  ButtonPrimary,
  ButtonGhost,
  Cursor,
  Sidebar,
  TopBar,
  AppShell,
  PhoneFrame,
  Toast,
};
